import { useEffect, useState } from 'react';

import { Box, Stack } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { EditDrawer } from 'common/components/EditDrawer';
import { DEFAULT_SUCCESS_MESSAGE } from 'common/components/EditDrawer/EditDrawer';
import {
    closeDrawer,
    selectCurrentDrawerId,
    selectNameInConflict,
    selectOpen,
    setNameInConflict,
    setSaveButtonEnabled
} from 'common/components/EditDrawer/editDrawerSlice';
import { DEFAULT_ERROR_HANDLER_INLINE_STYLES } from 'common/components/errorHandler/ErrorHandler';
import { PageInfo, selectPageInfo, selectPaginationGridData, setPaginationGridData } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { ReadOnlyTextField } from 'common/components/sharedCustomComponents/ReadOnlyTextField/ReadOnlyTextField';
import { IUser, UserProfileSearchDropdown } from 'common/components/sharedCustomComponents/userProfileSearchDropdown/UserProfileSearchDropdown';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchRoleUserPaginatedMemberships, postRoleUserMembership } from 'common/services/api/RoleUserMembership/RoleUserMembership.thunk';
import { EditDrawerContentProps } from 'common/types/TypeDefs';
import { DrawerIDs } from 'common/utils/DrawerIDs';
import { DEFAULT_ERROR_MESSAGE } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

import { selectFilters } from './roleUserMembershipSlice';

const constructCreateContentComponent = (
    title: string,
    pageInfo: PageInfo | undefined,
    saveButtonFunction: () => void,
    processing: boolean,
    userDetails: IUser | null,
    handleChange: (e: unknown, userDetails: IUser | null) => void,
    nameInConflict: boolean,
) => (
    <EditDrawer
        title={title}
        saveButtonFunction={saveButtonFunction}
        processing={processing}
    >
        <Stack spacing={2}>
            <UserProfileSearchDropdown
                value={userDetails}
                onChange={handleChange}
                hasError={nameInConflict}
            />
            {nameInConflict &&
                <Box component='div'
                    display='inline'
                    sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}
                >
                    This User is already linked to this Role.
                </Box>
            }
            <ReadOnlyTextField label="Tenant Name" value={pageInfo?.tenantName ?? ''} />
            <ReadOnlyTextField label="Role Name" value={pageInfo?.name ?? ''} />
        </Stack>
    </EditDrawer>
);

export function RoleUserMembershipCreateDrawerContent({
    title,
}: EditDrawerContentProps<{ name: string }>) {
    const paginationGridData = useAppSelector(selectPaginationGridData);
    const { currentPage = 0, rowsPerPage = 0, sortModel } = paginationGridData ?? {};
    const filterProp = useAppSelector(selectFilters);
    const { keywords } = filterProp;
    const [ userDetails, setUserDetails ] = useState<IUser | null>(null);
    const [processing, setProcessing] = useState<boolean>(false);
    const pageInfo = useAppSelector(selectPageInfo);
    const drawerOpen = useAppSelector(selectOpen);
    const currentId = useAppSelector(selectCurrentDrawerId);
    const { enqueueSnackbar } = useSnackbar();
    const nameInConflict = useAppSelector(selectNameInConflict);

    useEffect(() => {
        if(currentId === DrawerIDs.User){
            setUserDetails(null);
        }
    }, [drawerOpen]);

    const handleChange = (_e: unknown, user: IUser | null) => {
        if(user)
        {
            setUserDetails({
                id: user.id,
                profile: {
                    firstName: user.profile.firstName,
                    lastName: user.profile.lastName,
                    principalName: user.profile.principalName,
                    email: user.profile.email,
                    companyKey: user.profile.companyKey
                }
            });
        } else {
            setUserDetails(null);
        }
        dispatch(setNameInConflict(false));
        dispatch(setSaveButtonEnabled(!!user));
    };

    const dispatch = useAppDispatch();

    const userLinkPostCallback = () => {
        setProcessing(true);
        const roleOptions = {
            tenantId: pageInfo?.tenantId ?? '',
            roleId: pageInfo?.id ?? '',
            userId: userDetails?.id ?? '',
            paginationInfo: {
                skip: currentPage * rowsPerPage,
                take: rowsPerPage,
                search: keywords[0],
                sortModel,
            },
            status: filterProp.dropdowns.status.keys
        };
        dispatch(postRoleUserMembership(roleOptions))
            .unwrap()
            .then((result) => {
                enqueueSnackbar('The User was successfully linked.', { variant: 'success' });
                dispatch(setNameInConflict(false));
                dispatch(closeDrawer());
                dispatch(setPaginationGridData({
                    rows: result.rows,
                    rowsCount: result.count,
                    currentPage: result.skipped / rowsPerPage,
                    rowsPerPage,
                    sortModel,
                }));
            })
            .catch((error) => {
                dispatch(setNameInConflict(false));
                switch (extractStatusCode(error.message)) {
                    case 404: {
                        if (currentPage === 0) {
                            enqueueSnackbar('The User was successfully linked.', { variant: 'success' });
                            dispatch(setPaginationGridData({}));
                            dispatch(closeDrawer());
                        }
                        dispatch(fetchRoleUserPaginatedMemberships({
                            tenantId: pageInfo?.tenantId ?? '',
                            roleId: pageInfo?.id ?? '',
                            skip: 0,
                            take: rowsPerPage,
                            search: keywords[0],
                            sortKey: sortModel?.[0]?.field,
                            sortOrder: sortModel?.[0]?.sort,
                            status: filterProp.dropdowns.status.keys
                        }))
                            .unwrap()
                            .then((result) => {
                                dispatch(setPaginationGridData({
                                    rows: result.data,
                                    rowsCount: result.count,
                                    currentPage: result.skipped / rowsPerPage,
                                    rowsPerPage,
                                    sortModel,
                                }));
                            })
                            .catch(() => {
                                dispatch(setPaginationGridData({}));
                            })
                            .finally(() => {
                                enqueueSnackbar(DEFAULT_SUCCESS_MESSAGE, { variant: 'success' });
                                dispatch(closeDrawer());
                            });
                        break;
                    }
                    case 409: {
                        dispatch(setNameInConflict(true));
                        break;
                    }
                    default: {
                        enqueueSnackbar(
                            'There was an error while linking the User.' +
                            DEFAULT_ERROR_MESSAGE,
                            { variant: 'error' }
                        );
                        break;
                    }
                }
            })
            .finally(() => {
                setProcessing(false);
            });
    };

    return constructCreateContentComponent(
        title,
        pageInfo,
        userLinkPostCallback,
        processing,
        userDetails,
        handleChange,
        nameInConflict ?? false,
    );
}
